import { Avatar, Player } from '@/types';
import { AvatarCard } from './AvatarCard';
import './AvatarSelectionGrid.css';

interface AvatarSelectionGridProps {
  avatars: Avatar[];
  players: Player[];
  currentPlayerId: string;
  onSelectAvatar: (avatarId: string) => void;
  isLoading?: boolean;
  selectedAvatarId?: string | null;
}

export function AvatarSelectionGrid({
  avatars,
  players,
  currentPlayerId,
  onSelectAvatar,
  isLoading = false,
  selectedAvatarId = null,
}: AvatarSelectionGridProps) {
  // Get player name by ID
  const getPlayerName = (playerId: string): string => {
    const player = players.find(p => p.playerId === playerId);
    // Use playerName (chosen name) instead of username (Cognito ID)
    return player?.playerName || player?.username || 'Unknown Player';
  };

  // Get player username/ID by player ID
  const getPlayerUsername = (playerId: string): string | undefined => {
    const player = players.find(p => p.playerId === playerId);
    if (!player) return undefined;
    
    // Extract username from email (e.g., 'ekiss71' from 'ekiss71@hotmail.com')
    if (player.email) {
      return player.email.split('@')[0];
    }
    
    // If no email, check if username is a GUID (Cognito user ID)
    // GUIDs have the format: 12345678-1234-1234-1234-123456789012
    if (player.username && player.username.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      // For human players (not AI) with GUID username, return null so playerName is shown
      if (!player.isAI) {
        return undefined;
      }
    }
    
    // Fallback to username field
    return player.username;
  };

  // Check if current player has selected
  const currentPlayerHasSelected = avatars.some(
    a => a.selectedBy === currentPlayerId
  );

  // Count selections
  const selectedCount = avatars.filter(a => a.selectedBy !== null).length;
  const totalCount = avatars.length;

  return (
    <div className="avatar-selection-container">
      {/* Selection Status */}
      <div className="selection-status">
        <div className="status-text">
          {selectedCount === totalCount ? (
            <span className="status-complete">✓ All Players Ready!</span>
          ) : (
            <span>
              {selectedCount}/{totalCount} Avatars Selected
            </span>
          )}
        </div>
        {selectedCount < totalCount && (
          <div className="status-waiting">
            Waiting for players to select avatars...
          </div>
        )}
      </div>

      {/* Avatar Grid */}
      <div className="avatar-grid">
        {avatars.map((avatar) => {
          const isSelected = avatar.selectedBy !== null;
          const isSelectedByCurrentPlayer = avatar.selectedBy === currentPlayerId;
          // Allow selection if: not selected by anyone, OR selected by current player (reselect)
          // Backend will automatically unselect previous avatar when selecting a new one
          const isAvailable = !isSelected || isSelectedByCurrentPlayer;
          const selectedByPlayerName = avatar.selectedBy 
            ? getPlayerName(avatar.selectedBy)
            : undefined;
          const selectedByUsername = avatar.selectedBy
            ? getPlayerUsername(avatar.selectedBy)
            : undefined;

          return (
            <AvatarCard
              key={avatar.avatarId}
              avatar={avatar}
              isSelected={isSelected}
              isSelectedByCurrentPlayer={isSelectedByCurrentPlayer}
              isAvailable={isAvailable}
              selectedByPlayerName={selectedByPlayerName}
              selectedByUsername={selectedByUsername}
              onSelect={onSelectAvatar}
              isLoading={isLoading && selectedAvatarId === avatar.avatarId}
            />
          );
        })}
      </div>
    </div>
  );
}

